import { Button, Stack, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Add, DeleteForever, Person } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { licensePlateOptions } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/CommonOptions.tsx';
import { ReactHookFormSelectOption } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormSelect.tsx';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { getCustomerDisplayName } from 'dg-web-shared/common/models/Users.tsx';
import {
    ParkingaboUserBadgeType,
    ParkingaboVehicle,
    VehicleLicensePlateType,
    ParkingaboVehicleHistoryType,
    ParkingaboVehicleHistory,
} from 'dg-web-shared/common/models/Vehicle.tsx';
import { ShouldConfirmBeforeExitFormProps } from 'dg-web-shared/common/utils/FormDefinitions.tsx';
import { useEasyForm } from 'dg-web-shared/common/utils/FormHooksUtils.tsx';
import {
    RequestMethod,
    RequestStatus,
    ServerRequestState,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { Localized, useTranslation } from 'dg-web-shared/lib/Localization.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { ParkingaboUser } from './OperatorParkingaboUsersForm.tsx';
import { VehicleIconMap } from 'dg-web-shared/common/components/material-ui/VehicleTypeIcon.tsx';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage.tsx';
import { MaterialBar } from '../layout/components/MaterialBar.tsx';
import { useQuery } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { LicensePlateCountry } from 'dg-web-shared/dto/LicensePlateCountry.ts';
import { makeLocalizedText } from 'dg-web-shared/lib/Localized.ts';
import { Control, useController } from 'react-hook-form';
import {
    GenericFormSubmitError,
    ParkingaboTestValidationLink,
} from './ParkingaboUsersUtils.tsx';
import { BadgeConfirmationDialog } from './OperatorParkingaboUsersVehicleFormBadgeDetails.tsx';
import { OperatorFormSelect } from '../ui/form/OperatorFormSelect.tsx';
import DriveEta from '@mui/icons-material/DriveEta';
import {
    ParkingaboVehicleSearchResult,
    useUserVehiclesOutlet,
} from './OperatorParkingaboUsersVehicles.tsx';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { HeaderCancelSaveButtons } from '../layout/components/HeaderCancelSaveButtons.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import { getDisplayLabelForBadge } from 'product-shared/tenant/TenantVehicleIdentificationUtils';
import { CustomerTenantState } from 'product-shared/parkingabo/CustomerTenantState.ts';
import { mandatoryFieldErrorMessage } from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { BlockLabel } from '../layout/components/form/FormLayout.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { VehicleIdentification } from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';
import { ArchiveWarningEntryWithoutExitTooltip } from './OperatorParkingaboUsersDetail.tsx';

enum Fields {
    Description = 'description',
    LicensePlateNr = 'licensePlateNr',
    Type = 'type',
    Country = 'country',
    BadgeLabelNr = 'badgeLabelNr',
    BadgeType = 'badgeType',
}

export interface CreateVehiclePayload {
    [Fields.Description]: string;
    [Fields.LicensePlateNr]: string | null;
    [Fields.Type]: VehicleLicensePlateType | null;
    [Fields.Country]: string | null;
    [Fields.BadgeLabelNr]: string | null;
    [Fields.BadgeType]: ParkingaboUserBadgeType | null;
}

export function OperatorParkingaboUsersVehicleAddForm() {
    const { parkingaboUser, refetchList, refetchParkingaboUser } =
        useUserVehiclesOutlet();
    const query = useQuery();
    const vehiclesListUrl = `..?${query}`;
    const [createState, createRequest] = useServerWrite<
        CreateVehiclePayload,
        null,
        ValidationData
    >(() => ({
        url: `/ui-api/operator-account/parkingabo/customer/${parkingaboUser.customerNr}/vehicle`,
    }));

    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            backUrl={vehiclesListUrl}
            render={controller => (
                <OperatorParkingaboUsersVehicleForm
                    onSuccess={() => {
                        controller.setShouldConfirmBeforeLeave(false);
                        refetchList();
                        refetchParkingaboUser();
                        controller.safeNavigateTo(vehiclesListUrl);
                    }}
                    parkingaboUser={parkingaboUser}
                    onClose={controller.close}
                    onDirtyStateChange={controller.setShouldConfirmBeforeLeave}
                    submitState={createState}
                    onSubmit={createRequest}
                    isCreate={true}
                    readOnly={false}
                />
            )}
        />
    );
}

export function OperatorParkingaboUsersVehicleEditForm() {
    const {
        parkingaboUser,
        refetchList,
        vehiclesState,
        refetchParkingaboUser,
    } = useUserVehiclesOutlet();
    const query = useQuery();
    const vehiclesListUrl = `..?${query}`;

    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            backUrl={vehiclesListUrl}
            render={controller => (
                <OperatorParkingaboUsersVehicleEditFormContent
                    onSuccess={() => {
                        controller.setShouldConfirmBeforeLeave(false);
                        refetchList();
                        refetchParkingaboUser();
                        controller.safeNavigateTo(vehiclesListUrl);
                    }}
                    vehiclesState={vehiclesState}
                    parkingaboUser={parkingaboUser}
                    readOnly={
                        parkingaboUser.state !== CustomerTenantState.ACTIVE
                    }
                    onClose={controller.close}
                    onDirtyStateChange={controller.setShouldConfirmBeforeLeave}
                    onDeleteSuccess={() => {
                        controller.setShouldConfirmBeforeLeave(false);
                        refetchList();
                        refetchParkingaboUser();
                        controller.safeNavigateTo(vehiclesListUrl);
                    }}
                />
            )}
        />
    );
}

interface OperatorParkingaboUsersVehicleEditFormProps extends ShouldConfirmBeforeExitFormProps {
    onSuccess: () => void;
    vehiclesState: ServerRequestState<ParkingaboVehicleSearchResult, null>;
    parkingaboUser: ParkingaboUser;
    onDeleteSuccess: () => void;
    readOnly: boolean;
}

export function OperatorParkingaboUsersVehicleEditFormContent({
    onSuccess,
    onClose,
    onDirtyStateChange,
    parkingaboUser,
    vehiclesState,
    onDeleteSuccess,
    readOnly,
}: OperatorParkingaboUsersVehicleEditFormProps) {
    const params = useParams<{ vehicleId: string }>();

    const [editState, editRequest] = useServerWrite<
        CreateVehiclePayload,
        null,
        ValidationData
    >(() => ({
        url: `/ui-api/operator-account/parkingabo/customer/${parkingaboUser.customerNr}/vehicle/${params.vehicleId}`,
        method: RequestMethod.PUT,
    }));

    const [deleteState, deleteRequest] = useServerWrite<
        CreateVehiclePayload,
        null,
        ValidationData
    >(() => ({
        url: `/ui-api/operator-account/parkingabo/customer/${parkingaboUser.customerNr}/vehicle/${params.vehicleId}`,
        method: RequestMethod.DELETE,
    }));

    useServerSuccessEffect(deleteState, () => {
        onDeleteSuccess();
    });

    return (
        <OperatorAsyncLoadedSection
            requestState={vehiclesState}
            render={vehicles => {
                const vehicle = vehicles.results.find(
                    v => v.customerTenantCarId === params.vehicleId,
                );

                if (!vehicle) {
                    return (
                        <Alert severity="error">
                            <Localized
                                de="Fahrzeug nicht gefunden (Fehler 404)."
                                fr="Véhicule non trouvé (Erreur 404)."
                                it="Veicolo non trovato (Errore 404)."
                                en="Vehicle not found (Error 404)."
                            />
                        </Alert>
                    );
                }

                return (
                    <OperatorParkingaboUsersVehicleForm
                        onSuccess={onSuccess}
                        parkingaboUser={parkingaboUser}
                        onClose={onClose}
                        onDirtyStateChange={onDirtyStateChange}
                        submitState={editState}
                        vehicle={vehicle}
                        onSubmit={editRequest}
                        onDelete={deleteRequest}
                        deleteState={deleteState}
                        isCreate={false}
                        readOnly={readOnly}
                    />
                );
            }}
        />
    );
}

interface OperatorParkingaboUsersVehicleFormProps extends ShouldConfirmBeforeExitFormProps {
    parkingaboUser: ParkingaboUser;
    onSubmit: (formData: CreateVehiclePayload) => void;
    submitState: ServerRequestState<null, ValidationData>;
    vehicle?: ParkingaboVehicle;
    onSuccess: () => void;
    onDelete?: () => void;
    deleteState?: ServerRequestState<null, ValidationData>;
    isCreate: boolean;
    readOnly: boolean;
}

function getInitialValuesFromVehicle(vehicle?: ParkingaboVehicle) {
    if (vehicle) {
        return {
            [Fields.Description]: vehicle.description,
            [Fields.LicensePlateNr]: vehicle.licensePlateNr,
            [Fields.Type]: vehicle.type,
            [Fields.Country]: vehicle.country,
            [Fields.BadgeLabelNr]: vehicle.badgeLabelNr,
            [Fields.BadgeType]: vehicle.badgeType,
        };
    }
    return {
        [Fields.Description]: '',
        [Fields.LicensePlateNr]: null,
        [Fields.Type]: VehicleLicensePlateType.CAR,
        [Fields.Country]: 'CH',
        [Fields.BadgeLabelNr]: null,
        [Fields.BadgeType]: null,
    };
}

export function OperatorParkingaboUsersVehicleForm({
    parkingaboUser,
    submitState,
    vehicle,
    onDirtyStateChange,
    onSubmit,
    onClose,
    onSuccess,
    onDelete,
    deleteState,
    isCreate,
    readOnly,
}: OperatorParkingaboUsersVehicleFormProps) {
    const { language } = useLanguage();
    const {
        formInfo: { control, handleSubmit, reset, watch, setValue, trigger },
        formState: { isDirty },
        genericSubmitError,
    } = useEasyForm(onDirtyStateChange, submitState, language, {
        defaultValues: getInitialValuesFromVehicle(vehicle),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    useEffect(() => {
        if (vehicle) {
            reset(getInitialValuesFromVehicle(vehicle));
        }
    }, [reset, vehicle]);

    useServerSuccessEffect(submitState, () => {
        onSuccess();
    });

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showAddBadgeDialog, setShowAddBadgeDialog] = useState(false);
    const [showEditWithOpenEntryWarning, setShowEditWithOpenEntryWarning] =
        useState(false);
    const type = watch(Fields.Type);
    const country = watch(Fields.Country);
    const licensePlateNr = watch(Fields.LicensePlateNr);
    const badgeLabelNr = watch(Fields.BadgeLabelNr);
    const badgeType = watch(Fields.BadgeType);
    const translate = makeLocalizedText(language);
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    const [countriesState] = useServerFetch<LicensePlateCountry[], object>(
        () => ({
            url: `/ui-api/meta/license-plate-countries`,
        }),
        {},
    );
    const identificationMandatoryMessage = useTranslation({
        de: 'Kennzeichen oder Badge obligatorisch',
        fr: 'Immatriculation ou badge obligatoire',
        it: 'Targa o badge obbligatorio',
        en: 'Licence plate or badge mandatory',
    });
    const lpMandatoryMessage = useTranslation({
        de: 'Kennzeichen obligatorisch',
        fr: 'Immatriculation obligatoire',
        it: 'Targa obbligatorio',
        en: 'Licence plate mandatory',
    });
    const lpEnabled = parkingaboUser.role.vehicleIdentification.includes(
        VehicleIdentification.LICENSE_PLATE,
    );
    const badgeEnabled = parkingaboUser.role.vehicleIdentification.includes(
        VehicleIdentification.BADGE,
    );
    const lpRequired = lpEnabled && !badgeEnabled;
    const badgeRequired = badgeEnabled && !lpEnabled;
    const identificationMandatory =
        !licensePlateNr &&
        !badgeType &&
        [
            VehicleIdentification.LICENSE_PLATE,
            VehicleIdentification.BADGE,
        ].every(v => parkingaboUser.role.vehicleIdentification.includes(v));
    const missingBadgeMessage = badgeRequired
        ? mandatoryFieldMessage
        : identificationMandatoryMessage;
    useEffect(() => {
        if (!identificationMandatory) {
            trigger();
        }
    }, [identificationMandatory, trigger]);
    return (
        <OperatorAsyncLoadedSection
            requestState={countriesState}
            render={countryList => {
                const countryOptions = countryList
                    .sort((a, b) => (a.id <= b.id ? -1 : 1))
                    .map<ReactHookFormSelectOption<string>>(c => ({
                        value: c.id,
                        display: `${c.id} - ${translate(c.name)}`,
                    }));

                return (
                    <OperatorRoutedModalContent
                        header={
                            <HeaderComponent
                                Icon={type ? VehicleIconMap[type] : DriveEta}
                                title={
                                    !vehicle ? (
                                        <Localized
                                            de="Neues Fahrzeug"
                                            fr="Nouveau véhicule"
                                            it="Nuovo veicolo"
                                            en="New vehicle"
                                        />
                                    ) : (
                                        vehicle.description
                                    )
                                }
                                editMode={isDirty || isCreate}
                                buttons={
                                    <>
                                        {isDirty || isCreate ? (
                                            <HeaderCancelSaveButtons
                                                onCancel={
                                                    vehicle
                                                        ? () => reset()
                                                        : () => onClose()
                                                }
                                                onSave={() => {
                                                    if (
                                                        vehicle?.hasOpenEntryWithoutExit &&
                                                        (vehicle.badgeLabelNr !=
                                                            badgeLabelNr ||
                                                            vehicle.licensePlateNr !=
                                                                licensePlateNr ||
                                                            vehicle.country !=
                                                                country ||
                                                            vehicle.type !=
                                                                type)
                                                    ) {
                                                        setShowEditWithOpenEntryWarning(
                                                            true,
                                                        );
                                                    } else {
                                                        handleSubmit(
                                                            onSubmit,
                                                        )();
                                                    }
                                                }}
                                                loading={
                                                    submitState?.status ===
                                                    RequestStatus.PENDING
                                                }
                                                saveDisabled={!isDirty}
                                                noDiscardDialog={!vehicle}
                                            />
                                        ) : (
                                            <>
                                                {onDelete && (
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() =>
                                                            setShowDeleteDialog(
                                                                true,
                                                            )
                                                        }
                                                        disabled={
                                                            parkingaboUser.state !==
                                                            CustomerTenantState.ACTIVE
                                                        }
                                                        loading={
                                                            deleteState?.status ===
                                                            RequestStatus.PENDING
                                                        }
                                                    >
                                                        <Localized
                                                            de="Löschen"
                                                            fr="Effacer"
                                                            it="Elimina"
                                                            en="Delete"
                                                        />
                                                    </Button>
                                                )}
                                                <HeaderActionCloseButtons
                                                    onClose={onClose}
                                                    isLoading={
                                                        deleteState?.status ===
                                                        RequestStatus.PENDING
                                                    }
                                                />
                                            </>
                                        )}
                                        {deleteState && (
                                            <BackendRequestErrorMessage
                                                requestState={deleteState}
                                            />
                                        )}
                                        <DeleteVehicleDialog
                                            open={showDeleteDialog}
                                            onDelete={() => {
                                                setShowDeleteDialog(false);
                                                if (onDelete) {
                                                    onDelete();
                                                }
                                            }}
                                            onCancel={() =>
                                                setShowDeleteDialog(false)
                                            }
                                            vehicleDescription={watch(
                                                Fields.Description,
                                            )}
                                            hasOpenEntryWithoutExit={
                                                vehicle?.hasOpenEntryWithoutExit
                                            }
                                        />
                                        <EditVehicleWarning
                                            open={showEditWithOpenEntryWarning}
                                            onEdit={() => {
                                                setShowEditWithOpenEntryWarning(
                                                    false,
                                                );
                                                handleSubmit(onSubmit)();
                                            }}
                                            onCancel={() =>
                                                setShowEditWithOpenEntryWarning(
                                                    false,
                                                )
                                            }
                                        />
                                    </>
                                }
                            />
                        }
                        bar={
                            <MaterialBar
                                title={getCustomerDisplayName(
                                    parkingaboUser,
                                    false,
                                )}
                                Icon={Person}
                            />
                        }
                        body={
                            <Stack
                                sx={{
                                    flex: 1,
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Stack spacing={3}>
                                    <GenericFormSubmitError
                                        error={genericSubmitError}
                                        submitState={submitState}
                                    />
                                    <ReactHookFormTextField
                                        label={
                                            <Localized
                                                de="Beschreibung"
                                                fr="Description"
                                                it="Descrizione"
                                                en="Description"
                                            />
                                        }
                                        name={Fields.Description}
                                        control={control}
                                        readonly={readOnly}
                                        rules={{
                                            required: {
                                                value: true,
                                                message: mandatoryFieldMessage,
                                            },
                                        }}
                                    />
                                    <Grid container>
                                        <Grid size={4}>
                                            <ReactHookFormTextField
                                                label={
                                                    <Localized
                                                        de="Kennzeichen"
                                                        fr="Immatriculation"
                                                        it="Targa"
                                                        en="License plate"
                                                    />
                                                }
                                                name={Fields.LicensePlateNr}
                                                control={control}
                                                readonly={readOnly}
                                                forceUppercase
                                                onChange={event => {
                                                    if (event.target.value) {
                                                        if (!type) {
                                                            setValue(
                                                                Fields.Type,
                                                                VehicleLicensePlateType.CAR,
                                                            );
                                                        }
                                                        if (!country) {
                                                            setValue(
                                                                Fields.Country,
                                                                'CH',
                                                            );
                                                        }
                                                    }
                                                }}
                                                sx={{ width: '100%' }}
                                                rules={{
                                                    required: {
                                                        value:
                                                            lpRequired ||
                                                            (!badgeRequired &&
                                                                identificationMandatory),
                                                        message: lpRequired
                                                            ? lpMandatoryMessage
                                                            : identificationMandatoryMessage,
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        {Boolean(licensePlateNr) && (
                                            <>
                                                <Grid
                                                    size={4}
                                                    sx={{ paddingLeft: '15px' }}
                                                >
                                                    <OperatorFormSelect
                                                        label={
                                                            <Localized
                                                                de="Fahrzeugtyp"
                                                                fr="Type de véhicule"
                                                                it="Tipo veicolo"
                                                                en="Vehicle type"
                                                            />
                                                        }
                                                        name={Fields.Type}
                                                        control={control}
                                                        options={licensePlateOptions(
                                                            language,
                                                        )}
                                                        readonly={readOnly}
                                                        rules={{
                                                            required: {
                                                                value: Boolean(
                                                                    licensePlateNr,
                                                                ),
                                                                message:
                                                                    mandatoryFieldMessage,
                                                            },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid
                                                    size={4}
                                                    sx={{ paddingLeft: '15px' }}
                                                >
                                                    <OperatorFormSelect
                                                        options={countryOptions}
                                                        label={
                                                            <Localized
                                                                de="Land"
                                                                fr="Pays"
                                                                it="Paese"
                                                                en="Country"
                                                            />
                                                        }
                                                        name={Fields.Country}
                                                        control={control}
                                                        readonly={readOnly}
                                                        rules={{
                                                            required: {
                                                                value: Boolean(
                                                                    licensePlateNr,
                                                                ),
                                                                message:
                                                                    mandatoryFieldMessage,
                                                            },
                                                        }}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                    <FurtherIdentificationField
                                        control={control}
                                        isCreate={isCreate}
                                        vehicleIdentification={
                                            parkingaboUser.role
                                                .vehicleIdentification
                                        }
                                        onBadgeAdd={() =>
                                            setShowAddBadgeDialog(true)
                                        }
                                        onBadgeRemove={() => {
                                            setValue(
                                                Fields.BadgeLabelNr,
                                                null,
                                                {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                    shouldTouch: true,
                                                },
                                            );
                                            setValue(Fields.BadgeType, null, {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                                shouldTouch: true,
                                            });
                                        }}
                                        badgeType={badgeType}
                                        vehicle={vehicle}
                                        identificationMissing={
                                            badgeRequired ||
                                            identificationMandatory
                                        }
                                        missingBadgeMessage={
                                            missingBadgeMessage
                                        }
                                    />
                                </Stack>
                                <VehicleLastModifiedAt vehicle={vehicle} />
                                {showAddBadgeDialog && (
                                    <BadgeConfirmationDialog
                                        onAbort={() =>
                                            setShowAddBadgeDialog(false)
                                        }
                                        onSave={(labelNr, type) => {
                                            setValue(
                                                Fields.BadgeLabelNr,
                                                labelNr,
                                                {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                    shouldTouch: true,
                                                },
                                            );
                                            setValue(Fields.BadgeType, type, {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                                shouldTouch: true,
                                            });
                                            trigger();
                                            setShowAddBadgeDialog(false);
                                        }}
                                        parkingaboUser={parkingaboUser}
                                        language={language}
                                        badgeRequired={badgeRequired}
                                        mandatoryFieldMessage={
                                            mandatoryFieldMessage
                                        }
                                    />
                                )}
                            </Stack>
                        }
                    />
                );
            }}
        />
    );
}

function FurtherIdentificationField({
    control,
    vehicle,
    isCreate,
    onBadgeAdd,
    onBadgeRemove,
    badgeType,
    vehicleIdentification,
    identificationMissing,
    missingBadgeMessage,
}: {
    control: Control<CreateVehiclePayload, object>;
    vehicle?: ParkingaboVehicle;
    isCreate: boolean;
    onBadgeAdd: () => void;
    onBadgeRemove: () => void;
    badgeType: ParkingaboUserBadgeType | null;
    vehicleIdentification: VehicleIdentification[];
    identificationMissing: boolean;
    missingBadgeMessage: string;
}) {
    if (vehicleIdentification.includes(VehicleIdentification.QR)) {
        if (!isCreate) {
            return (
                <TextField
                    label={
                        <Localized
                            de="QR-Code ID"
                            fr="ID code QR"
                            it="ID codice QR"
                            en="QR code ID"
                        />
                    }
                    value={vehicle?.identificationQrCodeId ?? ''}
                    disabled={true}
                    slotProps={{
                        input: {
                            readOnly: true,
                            sx: {
                                borderBottom: theme =>
                                    `1px solid ${theme.palette.grey.A400}`,
                            },
                        },
                    }}
                />
            );
        }
    }
    if (vehicleIdentification.includes(VehicleIdentification.BADGE)) {
        return (
            <BadgeField
                control={control}
                badgeType={badgeType}
                isCreate={isCreate}
                onBadgeAdd={onBadgeAdd}
                onBadgeRemove={onBadgeRemove}
                identificationMissing={identificationMissing}
                missingBadgeMessage={missingBadgeMessage}
            />
        );
    }
}

function BadgeField({
    control,
    badgeType,
    isCreate,
    onBadgeAdd,
    onBadgeRemove,
    identificationMissing,
    missingBadgeMessage,
}: {
    control: Control<CreateVehiclePayload, object>;
    badgeType: ParkingaboUserBadgeType | null;
    isCreate: boolean;
    onBadgeAdd: () => void;
    onBadgeRemove: () => void;
    identificationMissing: boolean;
    missingBadgeMessage: string;
}) {
    const { language } = useLanguage();
    const {
        field: { value },
        fieldState: { error },
    } = useController({
        control: control,
        name: Fields.BadgeLabelNr,
        rules: {
            required: {
                value: identificationMissing,
                message: missingBadgeMessage,
            },
        },
    });
    return (
        <Grid container sx={{ marginRight: '24px' }}>
            <Grid size="grow">
                <TextField
                    label={
                        <Localized
                            de="Badge"
                            fr="Badge"
                            it="Badge"
                            en="Badge"
                        />
                    }
                    value={getDisplayLabelForBadge(language, value, badgeType)}
                    error={Boolean(error)}
                    helperText={error?.message}
                    disabled
                    fullWidth
                />
                {!isCreate && (
                    <ParkingaboTestValidationLink badgeLabelNr={value} />
                )}
            </Grid>
            <Grid
                sx={{
                    marginTop: 1,
                    alignItems: 'start',
                    display: 'flex',
                    gap: '16px',
                }}
            >
                <Button
                    startIcon={<Add />}
                    variant="outlined"
                    onClick={onBadgeAdd}
                    disabled={!!value}
                >
                    <Localized
                        de="Hinzufügen"
                        fr="Ajouter"
                        it="Aggiungi"
                        en="Add"
                    />
                </Button>
                <Button
                    startIcon={<DeleteForever />}
                    variant="outlined"
                    onClick={onBadgeRemove}
                    disabled={!value}
                >
                    <Localized
                        de="Entfernen"
                        fr="Retirer"
                        it="Rimuovi"
                        en="Remove"
                    />
                </Button>
            </Grid>
        </Grid>
    );
}

function VehicleLastModifiedAt({
    vehicle,
}: {
    vehicle: ParkingaboVehicle | undefined;
}) {
    if (!vehicle || !vehicle.lastModified) {
        return null;
    }

    return (
        <BlockLabel
            label={
                <Localized
                    de="Geändert am"
                    fr="Modifié le"
                    it="Modificato il"
                    en="Modified at"
                />
            }
        >
            {Formatter.dayMonthYearHourMinute(
                DateTime.fromISO(vehicle.lastModified.actionAt),
            )}
            {' ('}
            <VehicleLastModifiedTypeDisplay
                lastModified={vehicle.lastModified}
            />
            {')'}
        </BlockLabel>
    );
}

function VehicleLastModifiedTypeDisplay({
    lastModified,
}: {
    lastModified: ParkingaboVehicleHistory;
}) {
    switch (lastModified.type) {
        case ParkingaboVehicleHistoryType.OPERATOR_LOGIN:
            return (
                <>
                    <Localized
                        de={`Parkingportal ${EmDash} `}
                        fr={`Parkingportal ${EmDash} `}
                        it={`Parkingportal ${EmDash} `}
                        en={`Parkingportal ${EmDash} `}
                    />
                    {lastModified.modifiedBy}
                </>
            );
        case ParkingaboVehicleHistoryType.PARKINGABO_LOGIN:
            return (
                <Localized
                    de="Parkingabo-App"
                    fr="App Parkingabo"
                    it="App Parkingabo"
                    en="Parkingabo App"
                />
            );
        case ParkingaboVehicleHistoryType.EXTERNAL_ACCESS_TOKEN:
            return (
                <Localized
                    de="externer Zugriffs-Link"
                    fr="lien d'accès externe"
                    it="link di accesso esterno"
                    en="external access link"
                />
            );
        case ParkingaboVehicleHistoryType.UNKNOWN:
            return (
                <Localized
                    de="Unbekannt"
                    fr="Inconnu"
                    it="Sconosciuto"
                    en="Unknown"
                />
            );
    }
}

function DeleteVehicleDialog({
    open,
    onDelete,
    onCancel,
    vehicleDescription,
    hasOpenEntryWithoutExit,
}: {
    open: boolean;
    onDelete: () => void;
    onCancel: () => void;
    vehicleDescription: string;
    hasOpenEntryWithoutExit?: boolean;
}) {
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized de="Löschen" fr="Effacer" it="Elimina" en="Delete" />
            }
            onConfirm={onDelete}
            cancelLabel={
                <Localized
                    de="Abbrechen"
                    fr="Annuler"
                    it="Annulla"
                    en="Cancel"
                />
            }
            onCancel={onCancel}
            title={
                <Localized
                    de="Fahrzeug löschen"
                    fr="Supprimer véhicule"
                    it="Eliminare veicolo"
                    en="Delete vehicle"
                />
            }
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <p>
                <Localized
                    de={`Wollen Sie wirklich das Fahrzeug «${vehicleDescription}» löschen?`}
                    fr={`Voulez-vous vraiment effacer le véhicule «${vehicleDescription}»?`}
                    it={`Vuole veramente eliminare il veicolo «${vehicleDescription}»?`}
                    en={`Do you really want to delete the vehicle «${vehicleDescription}»?`}
                />
                {hasOpenEntryWithoutExit && (
                    <ul>
                        <li>
                            <b>
                                <Localized
                                    de="die laufende Transaktion wird beendet"
                                    fr="la transaction en cour sera terminée"
                                    it="la transazione in corso verrà terminata"
                                    en="the ongoing transaction will be terminated"
                                />
                            </b>
                            <ArchiveWarningEntryWithoutExitTooltip />
                        </li>
                    </ul>
                )}
            </p>
        </ConfirmationDialog>
    );
}

function EditVehicleWarning({
    open,
    onEdit,
    onCancel,
}: {
    open: boolean;
    onEdit: () => void;
    onCancel: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Parkvorgang beenden"
                    fr="Terminer la transaction"
                    it="Termina transazione"
                    en="End transaction"
                />
            }
            confirmLabel={
                <Localized
                    de="Weiter"
                    fr="Continuer"
                    it="Continua"
                    en="Continue"
                />
            }
            onConfirm={onEdit}
            cancelLabel={
                <Localized de="Zurück" fr="Retour" it="Indietro" en="Back" />
            }
            onCancel={onCancel}
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <>
                <p>
                    <Localized
                        de={
                            <>
                                Wenn Sie die Änderungen an diesem Fahrzeug
                                speichern,{' '}
                                <b>
                                    wird der entsprechende laufende Parkvorgang
                                    beendet.{' '}
                                </b>
                            </>
                        }
                        fr={
                            <>
                                Si vous sauvegardez les modifications apportées
                                à ce véhicule,{' '}
                                <b>la transaction en cours sera terminée.</b>
                            </>
                        }
                        it={
                            <>
                                Se salva le modifiche apportate a questo
                                veicolo,{' '}
                                <b>
                                    la relativa transazione in corso verrà
                                    interrotta.
                                </b>
                            </>
                        }
                        en={
                            <>
                                If you save the changes made to this vehicle,
                                the{' '}
                                <b>
                                    related transaction in progress will be
                                    stopped.
                                </b>
                            </>
                        }
                    />
                </p>
                <p>
                    <Localized
                        de="Sollte sich der Benutzer noch im Parking befinden, wird er an der Ausfahrt blockiert."
                        fr="Si l'utilisateur se trouve encore dans le parking, il sera bloqué à la sortie."
                        it="Se l'utente si trova ancora nel parcheggio, verrà bloccato all'uscita."
                        en="If the user is still in the parking lot, they will be blocked at the exit."
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}
